/**
 * Me Service
 * Current user profile (GET/PATCH /me) and avatar (POST/DELETE /me/avatar)
 */

import type { MeResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const ME_ENDPOINTS = {
  me: '/api/v1/me',
  avatar: '/api/v1/me/avatar',
} as const;

/**
 * Get current user profile (name, avatar URL).
 * Requires valid session.
 */
export async function getMe(): Promise<MeResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}${ME_ENDPOINTS.me}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      if (response.status === 401) return null;
      throw new Error(`Failed to get profile: ${response.statusText}`);
    }

    const data: MeResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Get me failed:', error);
    return null;
  }
}

/**
 * Update current user profile (first_name, last_name only).
 */
export async function updateMe(body: {
  first_name?: string | null;
  last_name?: string | null;
}): Promise<MeResponse> {
  const response = await fetch(`${API_BASE_URL}${ME_ENDPOINTS.me}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || `Update profile failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Upload current user avatar (multipart).
 * Allowed: image/jpeg, image/png, image/webp. Max size from backend config.
 */
export async function uploadAvatar(file: File): Promise<MeResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}${ME_ENDPOINTS.avatar}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    // Do not set Content-Type; browser sets multipart boundary
  });

  if (!response.ok) {
    if (response.status === 503) {
      throw new Error('Avatar upload is not configured');
    }
    if (response.status === 413) {
      throw new Error('File too large');
    }
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || `Upload failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Remove current user avatar.
 */
export async function deleteAvatar(): Promise<MeResponse> {
  const response = await fetch(`${API_BASE_URL}${ME_ENDPOINTS.avatar}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    if (response.status === 404) {
      // No profile or no avatar - treat as success
      const me = await getMe();
      if (me) return me;
    }
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || `Remove avatar failed: ${response.statusText}`);
  }

  return response.json();
}

export const meService = {
  getMe,
  updateMe,
  uploadAvatar,
  deleteAvatar,
};

export default meService;
